/**
 * Firestore Service Layer
 * Replaces localStorage with Cloud Firestore
 * Maintains the same API for backward compatibility
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { BabyData, FeedEntry, SleepEntry, DiaperEntry, GrowthEntry } from '../types';
import { StorageAdapter } from './storage-adapter';

const STORAGE_KEY = 'baby-tracker-data';
const CURRENT_VERSION = '1.0.0';

/**
 * Get user's Firestore collection references
 */
function getUserCollections(userId: string) {
  return {
    profile: doc(db, 'users', userId, 'profile', 'main'),
    feeds: collection(db, 'users', userId, 'feeds'),
    sleeps: collection(db, 'users', userId, 'sleeps'),
    diapers: collection(db, 'users', userId, 'diapers'),
    growth: collection(db, 'users', userId, 'growth'),
  };
}

/**
 * Convert Firestore Timestamp to Date
 */
function timestampToDate(timestamp: any): Date {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
}

/**
 * Initialize empty data structure
 */
function getEmptyData(): BabyData {
  return {
    feeds: [],
    sleeps: [],
    diapers: [],
    growth: [],
    version: CURRENT_VERSION,
    createdAt: new Date(),
    lastModified: new Date(),
  };
}

/**
 * Load all data from Firestore for a user
 */
export async function loadDataFromFirestore(userId: string): Promise<BabyData> {
  try {
    const collections = getUserCollections(userId);

    // Load all collections in parallel
    const [feedsSnap, sleepsSnap, diapersSnap, growthSnap] = await Promise.all([
      getDocs(query(collections.feeds, orderBy('timestamp', 'desc'))),
      getDocs(query(collections.sleeps, orderBy('startTime', 'desc'))),
      getDocs(query(collections.diapers, orderBy('timestamp', 'desc'))),
      getDocs(query(collections.growth, orderBy('date', 'desc'))),
    ]);

    const feeds: FeedEntry[] = feedsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: timestampToDate(doc.data().timestamp),
    })) as FeedEntry[];

    const sleeps: SleepEntry[] = sleepsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startTime: timestampToDate(doc.data().startTime),
      endTime: timestampToDate(doc.data().endTime),
    })) as SleepEntry[];

    const diapers: DiaperEntry[] = diapersSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: timestampToDate(doc.data().timestamp),
    })) as DiaperEntry[];

    const growth: GrowthEntry[] = growthSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: timestampToDate(doc.data().date),
    })) as GrowthEntry[];

    return {
      feeds,
      sleeps,
      diapers,
      growth,
      version: CURRENT_VERSION,
      createdAt: new Date(),
      lastModified: new Date(),
    };
  } catch (error) {
    console.error('Error loading data from Firestore:', error);
    return getEmptyData();
  }
}

/**
 * Create user profile on first login
 */
export async function createUserProfile(userId: string, email: string, name: string, babyName: string = 'Baby'): Promise<void> {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'main');
    await setDoc(profileRef, {
      email,
      name,
      babyName,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
}

/**
 * Get user profile
 */
export async function getUserProfile(userId: string): Promise<{ name: string; email: string; babyName: string } | null> {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'main');
    const profileSnap = await getDoc(profileRef);

    if (profileSnap.exists()) {
      return profileSnap.data() as { name: string; email: string; babyName: string };
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

/**
 * Migrate localStorage data to Firestore
 */
export async function migrateLocalDataToFirestore(userId: string, localData: BabyData): Promise<void> {
  try {
    const batch = writeBatch(db);
    const collections = getUserCollections(userId);

    // Migrate feeds
    localData.feeds.forEach((feed) => {
      const feedRef = doc(collections.feeds, feed.id);
      batch.set(feedRef, {
        ...feed,
        timestamp: Timestamp.fromDate(feed.timestamp),
        createdAt: serverTimestamp(),
      });
    });

    // Migrate sleeps
    localData.sleeps.forEach((sleep) => {
      const sleepRef = doc(collections.sleeps, sleep.id);
      batch.set(sleepRef, {
        ...sleep,
        startTime: Timestamp.fromDate(sleep.startTime),
        endTime: Timestamp.fromDate(sleep.endTime),
        createdAt: serverTimestamp(),
      });
    });

    // Migrate diapers
    localData.diapers.forEach((diaper) => {
      const diaperRef = doc(collections.diapers, diaper.id);
      batch.set(diaperRef, {
        ...diaper,
        timestamp: Timestamp.fromDate(diaper.timestamp),
        createdAt: serverTimestamp(),
      });
    });

    // Migrate growth
    localData.growth.forEach((growth) => {
      const growthRef = doc(collections.growth, growth.id);
      batch.set(growthRef, {
        ...growth,
        date: Timestamp.fromDate(growth.date),
        createdAt: serverTimestamp(),
      });
    });

    await batch.commit();
    console.log('✅ Successfully migrated local data to Firestore');
  } catch (error) {
    console.error('Error migrating data to Firestore:', error);
    throw error;
  }
}

/**
 * Add a new feed entry to Firestore
 */
export async function addFeedToFirestore(userId: string, feed: Omit<FeedEntry, 'id'>): Promise<FeedEntry> {
  try {
    const collections = getUserCollections(userId);
    const feedData = {
      ...feed,
      timestamp: Timestamp.fromDate(feed.timestamp),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.feeds, feedData);

    return {
      id: docRef.id,
      ...feed,
    };
  } catch (error) {
    console.error('Error adding feed to Firestore:', error);
    throw error;
  }
}

/**
 * Add a new sleep entry to Firestore
 */
export async function addSleepToFirestore(userId: string, sleep: Omit<SleepEntry, 'id'>): Promise<SleepEntry> {
  try {
    const collections = getUserCollections(userId);
    const sleepData = {
      ...sleep,
      startTime: Timestamp.fromDate(sleep.startTime),
      endTime: Timestamp.fromDate(sleep.endTime),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.sleeps, sleepData);

    return {
      id: docRef.id,
      ...sleep,
    };
  } catch (error) {
    console.error('Error adding sleep to Firestore:', error);
    throw error;
  }
}

/**
 * Add a new diaper entry to Firestore
 */
export async function addDiaperToFirestore(userId: string, diaper: Omit<DiaperEntry, 'id'>): Promise<DiaperEntry> {
  try {
    const collections = getUserCollections(userId);
    const diaperData = {
      ...diaper,
      timestamp: Timestamp.fromDate(diaper.timestamp),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.diapers, diaperData);

    return {
      id: docRef.id,
      ...diaper,
    };
  } catch (error) {
    console.error('Error adding diaper to Firestore:', error);
    throw error;
  }
}

/**
 * Add a new growth entry to Firestore
 */
export async function addGrowthToFirestore(userId: string, growth: Omit<GrowthEntry, 'id'>): Promise<GrowthEntry> {
  try {
    const collections = getUserCollections(userId);
    const growthData = {
      ...growth,
      date: Timestamp.fromDate(growth.date),
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collections.growth, growthData);

    return {
      id: docRef.id,
      ...growth,
    };
  } catch (error) {
    console.error('Error adding growth to Firestore:', error);
    throw error;
  }
}

/**
 * Delete an entry from Firestore
 */
export async function deleteEntryFromFirestore(
  userId: string,
  type: 'feeds' | 'sleeps' | 'diapers' | 'growth',
  id: string
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId, type, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting entry from Firestore:', error);
    throw error;
  }
}

/**
 * Update an entry in Firestore
 */
export async function updateEntryInFirestore(
  userId: string,
  type: 'feeds' | 'sleeps' | 'diapers' | 'growth',
  id: string,
  updates: any
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId, type, id);

    // Convert Date objects to Timestamps
    const processedUpdates = { ...updates };
    if (processedUpdates.timestamp instanceof Date) {
      processedUpdates.timestamp = Timestamp.fromDate(processedUpdates.timestamp);
    }
    if (processedUpdates.startTime instanceof Date) {
      processedUpdates.startTime = Timestamp.fromDate(processedUpdates.startTime);
    }
    if (processedUpdates.endTime instanceof Date) {
      processedUpdates.endTime = Timestamp.fromDate(processedUpdates.endTime);
    }
    if (processedUpdates.date instanceof Date) {
      processedUpdates.date = Timestamp.fromDate(processedUpdates.date);
    }

    await updateDoc(docRef, processedUpdates);
  } catch (error) {
    console.error('Error updating entry in Firestore:', error);
    throw error;
  }
}

/**
 * Load data from localStorage (fallback for offline)
 */
export function loadDataFromLocalStorage(): BabyData {
  try {
    const stored = StorageAdapter.getItem(STORAGE_KEY);
    if (!stored) {
      return getEmptyData();
    }
    const parsed = JSON.parse(stored);

    // Deserialize dates
    return {
      ...parsed,
      feeds: parsed.feeds.map((f: any) => ({
        ...f,
        timestamp: new Date(f.timestamp),
      })),
      sleeps: parsed.sleeps.map((s: any) => ({
        ...s,
        startTime: new Date(s.startTime),
        endTime: new Date(s.endTime),
      })),
      diapers: parsed.diapers.map((d: any) => ({
        ...d,
        timestamp: new Date(d.timestamp),
      })),
      growth: parsed.growth.map((g: any) => ({
        ...g,
        date: new Date(g.date),
      })),
      createdAt: new Date(parsed.createdAt),
      lastModified: new Date(parsed.lastModified),
    };
  } catch (error) {
    console.error('Error loading data from localStorage:', error);
    return getEmptyData();
  }
}

/**
 * Save data to localStorage (cache for offline)
 */
export function saveDataToLocalStorage(data: BabyData): void {
  try {
    const toSave = {
      ...data,
      lastModified: new Date(),
    };
    StorageAdapter.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving data to localStorage:', error);
  }
}
